import {
  AspectRatio,
  Group,
  Image,
  MultiSelect,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { CategoryDto, fetchCategories } from '../../../data/location.service'
import { DailyBusinessHours } from '../../../models/DailyBusinessHours'
import { OnboardingDevice } from '../../../models/Onboarding/OnboardingDevice'
import { OnboardingLocation } from '../../../models/Onboarding/OnboardingLocation'
import {
  DAYSOFWEEK,
  HostOnboardingSteps,
  OnboardingWizardPages,
} from '../../../utils/constants'
import {
  formatAddress,
  formatApprovalType,
  formatNumber,
} from '../../../utils/displayUtils'
import BusinessHoursComponent from '../../BusinessHours'
import classes from '../../css/ReviewStep.module.css'
import PencilEditButton from '../../PencilEditButton'

const footTrafficRanges = {
  low: '0–50',
  moderate: '51–150',
  steady: '151–300',
  busy: '301–600',
  high: '601+',
}

// Function to sort business hours in correct order
const sortBusinessHours = (
  businessHours: Record<string, DailyBusinessHours>,
) => {
  const sortedHours: Record<string, DailyBusinessHours> = {}

  // Always return in correct order: Monday through Sunday
  DAYSOFWEEK.forEach((day) => {
    if (businessHours[day]) {
      sortedHours[day] = businessHours[day]
    }
  })

  return sortedHours
}

interface ReviewStepProps {
  location: OnboardingLocation
  device: OnboardingDevice
  setCurrentStep: (step: HostOnboardingSteps) => void
  pageName: OnboardingWizardPages
}

function ReviewStep(props: ReviewStepProps) {
  const { location, device, setCurrentStep, pageName } = props
  const [categories, setCategories] = useState<CategoryDto[]>([])

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetchedCategories = await fetchCategories()
        setCategories(fetchedCategories)
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }

    loadCategories()
  }, [])

  // Sort business hours to ensure correct order
  const sortedBusinessHours = sortBusinessHours(location.businessHours)

  return (
    <Stack ml='5%' mr='5%' justify='flex-start'>
      <Title>Review Details</Title>
      <Stack gap={50}>
        {(pageName === OnboardingWizardPages.Onboarding ||
          pageName === OnboardingWizardPages.ManageLocation) && (
          <Group align='flex-start' gap={30}>
            <AspectRatio ratio={4 / 3} flex='3 1 600px'>
              <div className={classes.imageContainer}>
                <Image src={location.imageUrls[0]} className={classes.image} />
                <PencilEditButton
                  onClick={() =>
                    setCurrentStep(HostOnboardingSteps.LocationPhotos)
                  }
                  style={{ backgroundColor: 'white' }}
                  className={classes.editButton}
                />
              </div>
            </AspectRatio>
            <Stack flex='2 1 300px'>
              <Group gap={15} wrap='nowrap' align='center'>
                <Title order={2} style={{ wordBreak: 'break-word' }}>
                  {location.name}
                </Title>
                <PencilEditButton
                  onClick={() =>
                    setCurrentStep(HostOnboardingSteps.LocationName)
                  }
                />
              </Group>
              <Stack gap={0}>
                <Group gap={5} wrap='nowrap'>
                  <Text className={classes.labelText}>Description</Text>
                  <PencilEditButton
                    onClick={() =>
                      setCurrentStep(HostOnboardingSteps.LocationDescription)
                    }
                  />
                </Group>
                <Text>{location.description}</Text>
              </Stack>
              <Stack gap={0}>
                <Group gap={5} wrap='nowrap'>
                  <Text className={classes.labelText}>Address</Text>
                  <PencilEditButton
                    onClick={() => setCurrentStep(HostOnboardingSteps.Address)}
                  />
                </Group>
                {formatAddress(location).map((line, index) => (
                  <Text key={index}>{line}</Text>
                ))}
              </Stack>
              <Stack gap={0}>
                <Group gap={5} wrap='nowrap'>
                  <Text className={classes.labelText}>Business Hours</Text>
                  <PencilEditButton
                    onClick={() =>
                      setCurrentStep(HostOnboardingSteps.SetBusinessHours)
                    }
                  />
                </Group>
                <BusinessHoursComponent businessHours={sortedBusinessHours} />
              </Stack>
              {location.footTraffic && (
                <Stack gap={0}>
                  <Group gap={5} wrap='nowrap'>
                    <Text className={classes.labelText}>Foot Traffic</Text>
                    <PencilEditButton
                      onClick={() =>
                        setCurrentStep(HostOnboardingSteps.FootTraffic)
                      }
                    />
                  </Group>
                  <Text>
                    {location.footTraffic.charAt(0).toUpperCase() +
                      location.footTraffic.slice(1)}{' '}
                    (
                    {footTrafficRanges[
                      location.footTraffic as keyof typeof footTrafficRanges
                    ] || ''}
                    )
                  </Text>
                </Stack>
              )}
            </Stack>
          </Group>
        )}
        {(pageName === OnboardingWizardPages.Onboarding ||
          pageName === OnboardingWizardPages.ManageDevice) && (
          <Group align='flex-start' gap={30}>
            <Stack flex='3 1 600px'>
              <AspectRatio ratio={4 / 3}>
                <div className={classes.imageContainer}>
                  <Image
                    src={device!.imageUrl}
                    alt={device!.name || 'Device'}
                    className={classes.image}
                  />
                  <PencilEditButton
                    onClick={() =>
                      setCurrentStep(HostOnboardingSteps.DevicePhotos)
                    }
                    style={{ backgroundColor: 'white' }}
                    className={classes.editButton}
                  />
                </div>
              </AspectRatio>
            </Stack>
            <Stack flex='2 1 300px'>
              <Group gap={15} wrap='nowrap'>
                <Title>{device!.name}</Title>
                <PencilEditButton
                  onClick={() => setCurrentStep(HostOnboardingSteps.DeviceName)}
                />
              </Group>
              <Stack gap={0}>
                <Group gap={5}>
                  <Text className={classes.labelText}>Price</Text>
                  <PencilEditButton
                    onClick={() =>
                      setCurrentStep(HostOnboardingSteps.SetPricing)
                    }
                  />
                </Group>
                <Text>${formatNumber(device?.monthlyPrice ?? 0)}/month</Text>
              </Stack>
              <Stack gap={0}>
                <Group gap={5}>
                  <Text className={classes.labelText}>Size</Text>
                  <PencilEditButton
                    onClick={() =>
                      setCurrentStep(HostOnboardingSteps.DeviceSize)
                    }
                  />
                </Group>
                <Text>{device!.screenSize} inches</Text>
              </Stack>
              <Stack gap={0}>
                <Group gap={5}>
                  <Text className={classes.labelText}>Approval</Text>
                  <PencilEditButton
                    onClick={() =>
                      setCurrentStep(HostOnboardingSteps.ApprovalType)
                    }
                  />
                </Group>
                <Text>{formatApprovalType(device!.approvalType)}</Text>
              </Stack>

              {device?.restrictedCategoryIds.length > 0 && (
                <Stack gap={0}>
                  <Group gap={5}>
                    <Text className={classes.labelText}>
                      Restricted Categories
                    </Text>
                    <PencilEditButton
                      onClick={() =>
                        setCurrentStep(
                          HostOnboardingSteps.AdvertiserRestrictions,
                        )
                      }
                    />
                  </Group>
                  <MultiSelect
                    maw={600}
                    placeholder='Select all that apply'
                    data={categories.map((c) => ({
                      label: c.name,
                      value: c.id.toString(),
                    }))}
                    value={device?.restrictedCategoryIds.map((id) =>
                      id.toString(),
                    )}
                    disabled
                  />
                </Stack>
              )}
            </Stack>
          </Group>
        )}
      </Stack>
    </Stack>
  )
}

export default ReviewStep
